/**
 * MultiPartyVideoView — multi-participant call UI.
 *
 * Activated whenever a call has 3+ participants (self + 2 or more remote
 * peers). Replaces the single-feed VideoCallContainer body. Spec from the
 * user (iter147), captured verbatim in PRD.md — implementation MUST NOT
 * deviate:
 *
 * Desktop / iPad-landscape:
 *   • Top of screen has a SLIDABLE row of mini-tiles, one per participant.
 *   • Each mini-tile is SQUARE — 80% of the current single-call self-PIP
 *     edge (128 × 0.8 ≈ 102px). Landscape feeds fill perfectly; portrait
 *     feeds get vertical letterbox bars left + right (object-contain on
 *     black bg).
 *   • Each mini-tile has an Expand button in its top-right corner.
 *     Clicking it makes that participant the dominant/main video.
 *   • Expand works on the user's OWN tile too.
 *   • A view-toggle button in the top-right of the page switches between
 *     "highlight" (current spotlight layout) and "split" (grid).
 *   • In highlight mode, the spotlighted participant's name shows in the
 *     top-right corner of the main video.
 *
 * Split mode (desktop only — mobile is spotlight-only):
 *   • Grid layouts driven STRICTLY by participant count:
 *       2:  half/half       (1 × 2)
 *       3:  3 × 1           (no spec; mirrors 2's row layout for visual continuity)
 *       4:  2 × 2
 *       5:  3 × 2 with 1 empty slot (bottom-right)
 *       6:  3 × 2
 *       7:  3 × 3 with 2 empty slots (bottom-right)
 *       8:  3 × 3 with 1 empty slot (bottom-right)
 *       9:  3 × 3
 *       10: 4 × 3 with 2 empty slots (bottom-right)
 *       11: 4 × 3 with 1 empty slot (bottom-right)
 *       12: 4 × 3
 *   • 13+: paginate at 12 per page. Buttons to scroll prev/next.
 *   • Empty slots are blurred (frosted glass).
 *
 * Mobile:
 *   • Spotlight only — no split-mode toggle.
 *   • Top row reserved for participant mini-tiles (PORTRAIT aspect, 9:16,
 *     same ~102 × 141 sizing as the legacy self-PIP).
 *   • Default main feed = first user joined (i.e., the original caller's
 *     remote). New joiners get appended to the right of the strip; the
 *     user has to tap Expand to switch the main feed.
 *   • If the currently-spotlighted user disconnects, fall back to the
 *     PREVIOUS spotlighted user. If none, pick a random remaining one.
 *
 * Aspect handling (driven by viewer's device + orientation):
 *   • Desktop OR (iPad/mobile) in LANDSCAPE → square tiles, square main
 *     feed; landscape source video fills, portrait source gets vertical
 *     letterbox bars.
 *   • Mobile/iPad in PORTRAIT → portrait tiles (9:16), portrait main
 *     feed; portrait source fills, landscape source gets horizontal
 *     letterbox bars.
 *   • Letterbox is achieved with `object-contain` on a black-bg parent.
 *
 * This component DOES NOT touch any peer connection logic — it only
 * arranges the existing streams (localStream, remoteStream,
 * secondaryRemoteStream) into the spec'd layout.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Maximize2, LayoutGrid, Columns, ChevronLeft, ChevronRight } from 'lucide-react';

// --- constants -------------------------------------------------------------
// 80% of the current self-PIP w-32 h-44 (128 × 176). Two tile shapes:
//   • SQUARE for landscape/desktop contexts
//   • PORTRAIT for mobile-portrait/iPad-portrait
const TILE_SIDE_SQUARE = 102;            // 128 * 0.8
const TILE_W_PORTRAIT = 102;             // 128 * 0.8
const TILE_H_PORTRAIT = 141;             // 176 * 0.8

// Hook — true when the viewport is in landscape orientation OR wider than
// 1024px (treat large desktops as "landscape" even when window aspect is
// near-square).
const useIsLandscapeLayout = () => {
  const [v, setV] = useState(() => {
    if (typeof window === 'undefined') return true;
    const isWide = window.innerWidth >= 1024;
    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    return isWide || isLandscape;
  });
  useEffect(() => {
    const onChange = () => {
      const isWide = window.innerWidth >= 1024;
      const isLandscape = window.matchMedia('(orientation: landscape)').matches;
      setV(isWide || isLandscape);
    };
    const mql = window.matchMedia('(orientation: landscape)');
    window.addEventListener('resize', onChange);
    if (mql.addEventListener) mql.addEventListener('change', onChange); else mql.addListener(onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      if (mql.removeEventListener) mql.removeEventListener('change', onChange); else mql.removeListener(onChange);
    };
  }, []);
  return v;
};

// Hook — true when window is below the mobile breakpoint (768px).
const useIsMobileViewport = () => {
  const [v, setV] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const onChange = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', onChange);
    return () => window.removeEventListener('resize', onChange);
  }, []);
  return v;
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleGradient = (role) => {
  switch (role) {
    case 'admin': return 'from-rose-500 to-pink-600';
    case 'supervisor': return 'from-blue-500 to-indigo-600';
    case 'lead': return 'from-emerald-500 to-teal-600';
    default: return 'from-slate-500 to-slate-600';
  }
};

// Build the participant list with each entry's stream (if any) and a
// stable identity. Participants without a stream still render — they
// show their avatar instead of a video element.
//
// Sources:
//   • Self (callState.participants where is_caller matches isCaller flag) → localStream
//   • Primary remote (the OTHER participant in callState.participants) → remoteStream
//   • Secondary remote (callState._pendingWarmTransfer.targetUserId, if any) → secondaryRemoteStream
// Anything beyond those three is currently unsupported by the WebRTC mesh
// but we still render an avatar placeholder for forward-compat.
const useResolvedParticipants = (callState, mediaState, user) => {
  return useMemo(() => {
    const out = [];
    const selfId = user?.id;
    const selfParticipant = (callState.participants || []).find((p) => p.user_id === selfId);
    const remoteParticipants = (callState.participants || []).filter((p) => p.user_id !== selfId);
    if (selfParticipant) {
      out.push({
        id: selfParticipant.user_id || 'self',
        name: selfParticipant.name || 'You',
        role: selfParticipant.role,
        profile_picture: selfParticipant.profile_picture,
        stream: mediaState.localStream,
        isSelf: true,
        mirror: true,
      });
    }
    remoteParticipants.forEach((p, idx) => {
      out.push({
        id: p.user_id,
        name: p.name,
        role: p.role,
        profile_picture: p.profile_picture,
        // The primary remote stream maps to the first (and only) remote in a 2-party
        // call. In a 3-way conference with a secondary peer, the SECOND remote
        // (added via Add Participant / warm transfer) gets the secondaryRemoteStream.
        stream: idx === 0 ? mediaState.remoteStream : null,
        isSelf: false,
        mirror: false,
      });
    });
    // Secondary remote (warm-transfer / add-participant target). After
    // a merge, `_pendingWarmTransfer` is null'd but `secondaryRemoteStream`
    // remains live, so we still want the secondary tile visible. Fall
    // back to a synthetic participant whenever the stream exists but
    // we don't have richer info.
    const sec = callState._pendingWarmTransfer;
    if (sec?.targetUserId) {
      out.push({
        id: sec.targetUserId,
        name: sec.targetName || 'Teammate',
        role: sec.targetRole,
        profile_picture: sec.targetProfilePicture,
        stream: mediaState.secondaryRemoteStream,
        isSelf: false,
        mirror: false,
      });
    } else if (mediaState.secondaryRemoteStream) {
      out.push({
        id: 'secondary',
        name: 'Teammate',
        role: null,
        profile_picture: null,
        stream: mediaState.secondaryRemoteStream,
        isSelf: false,
        mirror: false,
      });
    }
    return out;
  }, [callState.participants, callState._pendingWarmTransfer, mediaState.localStream, mediaState.remoteStream, mediaState.secondaryRemoteStream, user?.id]);
};

// Sub-component — a single video tile bound to a participant's MediaStream
// (or avatar fallback if no stream). Used in both mini-tile rows and grid
// cells.
const ParticipantTile = ({ p, onExpand, showExpand = true, shape = 'square', isMain = false, isActive = false, testId }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && p?.stream) {
      videoRef.current.srcObject = p.stream;
    }
  }, [p?.stream]);
  const hasVideo = !!(p?.stream && p.stream.getVideoTracks && p.stream.getVideoTracks().length > 0);
  const inlineSize = !isMain ? (
    shape === 'portrait'
      ? { width: TILE_W_PORTRAIT, height: TILE_H_PORTRAIT, flex: '0 0 auto' }
      : { width: TILE_SIDE_SQUARE, height: TILE_SIDE_SQUARE, flex: '0 0 auto' }
  ) : null;
  return (
    <div
      className={`relative bg-black overflow-hidden ${isMain ? 'w-full h-full' : 'rounded-xl border-2 ' + (isActive ? 'border-[#586D52]' : 'border-white/15')}`}
      style={inlineSize || undefined}
      data-testid={testId}
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          // Mute self unconditionally (we never want to hear our own mic
          // played back). For remote participants, mute the MINI-TILE
          // <video> so audio plays only from the main spotlight feed —
          // otherwise the same MediaStream attached to both the mini
          // tile AND the main feed would double the audio.
          muted={!!p?.isSelf || !isMain}
          // object-contain so non-matching aspects letterbox (spec).
          className="w-full h-full object-contain"
          style={p?.mirror ? { transform: 'scaleX(-1)' } : undefined}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
          <Avatar className={isMain ? 'w-32 h-32 border-4 border-white/10' : 'w-12 h-12'}>
            <AvatarImage src={p?.profile_picture} />
            <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(p?.role)} text-white ${isMain ? 'text-3xl' : 'text-sm'}`}>
              {getInitials(p?.name)}
            </AvatarFallback>
          </Avatar>
        </div>
      )}
      {/* Expand button — top-right corner of each mini tile. */}
      {showExpand && !isMain && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onExpand?.(p); }}
          className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white"
          aria-label={`Expand ${p?.name}`}
          data-testid={`mp-expand-${p?.id}`}
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      )}
      {/* Name label on mini tile bottom edge. */}
      {!isMain && (
        <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-gradient-to-t from-black/80 to-transparent">
          <p className="text-white text-[10px] font-medium truncate">{p?.name}{p?.isSelf ? ' (you)' : ''}</p>
        </div>
      )}
    </div>
  );
};

// --- main component -------------------------------------------------------
const MultiPartyVideoView = ({ callState, mediaState, user }) => {
  const isLandscape = useIsLandscapeLayout();
  const isMobile = useIsMobileViewport();
  const participants = useResolvedParticipants(callState, mediaState, user);

  // Default main feed = first user joined (i.e., the original primary
  // remote — index 1 in our resolved list since index 0 is self). Spec:
  // "Default to the first user joined".
  const [spotlightId, setSpotlightId] = useState(null);
  const [previousSpotlightId, setPreviousSpotlightId] = useState(null);
  // 'highlight' | 'split'. Mobile is forced to 'highlight' per spec.
  const [viewMode, setViewMode] = useState('highlight');
  const effectiveViewMode = isMobile ? 'highlight' : viewMode;
  const [pageIndex, setPageIndex] = useState(0);

  // Resolve actual spotlight: chosen id → fallback to default first remote.
  const defaultSpotlight = useMemo(() => {
    const firstRemote = participants.find((p) => !p.isSelf);
    return firstRemote?.id || participants[0]?.id || null;
  }, [participants]);

  // If the currently-spotlighted participant disappears (disconnect), fall
  // back to the PREVIOUS spotlight id. If none, pick a random remaining
  // participant. Spec verbatim.
  useEffect(() => {
    if (!spotlightId) return;
    const stillThere = participants.some((p) => p.id === spotlightId);
    if (stillThere) return;
    if (previousSpotlightId && participants.some((p) => p.id === previousSpotlightId)) {
      setSpotlightId(previousSpotlightId);
      return;
    }
    const candidates = participants.filter((p) => !p.isSelf);
    if (candidates.length === 0) {
      setSpotlightId(null);
      return;
    }
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    setSpotlightId(pick.id);
  }, [participants, spotlightId, previousSpotlightId]);

  const activeSpotlight = spotlightId || defaultSpotlight;
  const mainParticipant = useMemo(
    () => participants.find((p) => p.id === activeSpotlight) || null,
    [participants, activeSpotlight]
  );

  const handleExpand = (p) => {
    setPreviousSpotlightId(activeSpotlight);
    setSpotlightId(p.id);
  };

  // Split-mode helpers -----------------------------------------------------
  // Strictly per the user's grid spec. cols × rows × empties.
  const splitLayout = useMemo(() => {
    const n = participants.length;
    if (n <= 2) return { cols: 2, rows: 1, slots: 2 };
    if (n === 3) return { cols: 3, rows: 1, slots: 3 };
    if (n === 4) return { cols: 2, rows: 2, slots: 4 };
    if (n === 5) return { cols: 3, rows: 2, slots: 6 };  // -1 empty
    if (n === 6) return { cols: 3, rows: 2, slots: 6 };
    if (n === 7) return { cols: 3, rows: 3, slots: 9 };  // -2 empties
    if (n === 8) return { cols: 3, rows: 3, slots: 9 };  // -1 empty
    if (n === 9) return { cols: 3, rows: 3, slots: 9 };
    if (n === 10) return { cols: 4, rows: 3, slots: 12 }; // -2 empties
    if (n === 11) return { cols: 4, rows: 3, slots: 12 }; // -1 empty
    return { cols: 4, rows: 3, slots: 12 };  // 12+ → paginate at 12/page
  }, [participants.length]);

  const totalPages = Math.max(1, Math.ceil(participants.length / 12));
  useEffect(() => {
    if (pageIndex >= totalPages) setPageIndex(0);
  }, [pageIndex, totalPages]);
  const pageStart = pageIndex * 12;
  const pageParticipants = participants.slice(pageStart, pageStart + 12);
  const emptySlots = Math.max(0, splitLayout.slots - pageParticipants.length);

  const tileShape = isLandscape ? 'square' : 'portrait';

  // --- HIGHLIGHT MODE -----------------------------------------------------
  const renderHighlight = () => (
    <div className="absolute inset-0 flex flex-col">
      {/* Top: slidable row of mini-tiles */}
      <div
        className="relative flex items-center gap-2 p-3 overflow-x-auto overflow-y-hidden bg-black/40 backdrop-blur-sm"
        style={{ scrollbarWidth: 'thin' }}
        data-testid="mp-mini-strip"
      >
        {participants.map((p) => (
          <ParticipantTile
            key={p.id}
            p={p}
            onExpand={handleExpand}
            shape={tileShape}
            isActive={p.id === activeSpotlight}
            testId={`mp-mini-${p.id}`}
          />
        ))}
        {/* View toggle — desktop only (mobile is spotlight-only) */}
        {!isMobile && (
          <button
            type="button"
            onClick={() => setViewMode((m) => (m === 'highlight' ? 'split' : 'highlight'))}
            className="ml-auto sticky right-2 flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-xs font-medium"
            aria-label="Toggle view mode"
            data-testid="mp-view-toggle"
          >
            <LayoutGrid className="w-4 h-4" />
            <span>Split view</span>
          </button>
        )}
      </div>
      {/* Main spotlight feed below */}
      <div className="flex-1 relative bg-black">
        {mainParticipant && (
          <ParticipantTile
            p={mainParticipant}
            isMain
            showExpand={false}
            shape={tileShape}
            testId="mp-main-feed"
          />
        )}
        {/* Name in top-right corner of main video — only when a feed is spotlighted */}
        {mainParticipant && (
          <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-sm text-white text-sm font-medium" data-testid="mp-main-name">
            {mainParticipant.name}{mainParticipant.isSelf ? ' (you)' : ''}
          </div>
        )}
      </div>
    </div>
  );

  // --- SPLIT MODE ---------------------------------------------------------
  const renderSplit = () => (
    <div className="absolute inset-0 flex flex-col" data-testid="mp-split-view">
      {/* Page header with view toggle + pagination */}
      <div className="flex items-center gap-2 p-3 bg-black/40 backdrop-blur-sm">
        {totalPages > 1 && (
          <>
            <button
              type="button"
              disabled={pageIndex === 0}
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
              aria-label="Previous page"
              data-testid="mp-split-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white text-xs font-medium">Page {pageIndex + 1} / {totalPages}</span>
            <button
              type="button"
              disabled={pageIndex >= totalPages - 1}
              onClick={() => setPageIndex((i) => Math.min(totalPages - 1, i + 1))}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white"
              aria-label="Next page"
              data-testid="mp-split-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setViewMode('highlight')}
          className="ml-auto flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-xs font-medium"
          aria-label="Toggle view mode"
          data-testid="mp-view-toggle"
        >
          <Columns className="w-4 h-4" />
          <span>Highlight view</span>
        </button>
      </div>
      {/* Grid */}
      <div
        className="flex-1 grid gap-2 p-2 bg-black"
        style={{
          gridTemplateColumns: `repeat(${splitLayout.cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${splitLayout.rows}, minmax(0, 1fr))`,
        }}
        data-testid="mp-split-grid"
      >
        {pageParticipants.map((p) => (
          <div key={p.id} className="relative bg-black overflow-hidden rounded-xl border border-white/10" data-testid={`mp-split-cell-${p.id}`}>
            <ParticipantTile p={p} isMain showExpand={false} shape={tileShape} testId={`mp-split-tile-${p.id}`} />
            <div className="absolute bottom-1 left-2 px-2 py-0.5 bg-black/55 rounded text-white text-xs">
              {p.name}{p.isSelf ? ' (you)' : ''}
            </div>
          </div>
        ))}
        {/* Empty slots (blurred frosted glass), always bottom-right per spec. */}
        {Array.from({ length: emptySlots }).map((_, idx) => (
          <div
            key={`empty-${idx}`}
            className="relative overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-md"
            data-testid={`mp-split-empty-${idx}`}
          />
        ))}
      </div>
    </div>
  );

  return effectiveViewMode === 'split' ? renderSplit() : renderHighlight();
};

export default MultiPartyVideoView;
